import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useUIStore } from '../../store/uiStore';

import api from '../../services/api';

const pageNames = {
  '/': 'Dashboard',
  '/leads': 'Lead Management',
  '/contacts': 'Contacts Page',
  '/site-visits': 'Site Visits',
  '/design': 'Design Studio',
  '/projects': 'Project Management',
  '/inventory': 'Inventory',
  '/accounts': 'Accounts & Payments',
  '/analytics': 'Analytics',
  '/users': 'Team Management',
  '/settings': 'Settings',
  '/home': 'Verve Kitchens',
  '/approvals': 'Approvals',
  '/notifications': 'Notifications',
  '/documents': 'Documents',
};

export const Header = ({ sidebarCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { pageTitle } = useUIStore();
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  React.useEffect(() => {
    const fetchNotifs = async () => {
      try {
        const res = await api.get('/notifications?limit=5');
        if (res.data?.success) {
          setNotifications(res.data.data);
          setUnreadCount(res.data.unreadCount);
        }
      } catch (err) {
        console.error("Failed to fetch notifications:", err);
      }
    };
    fetchNotifs();
    // Refresh periodically if desired
    const interval = setInterval(fetchNotifs, 60000);
    
    // Listen for manual refetch triggers
    const handleRefetch = () => fetchNotifs();
    window.addEventListener('refetchNotifications', handleRefetch);

    return () => {
      clearInterval(interval);
      window.removeEventListener('refetchNotifications', handleRefetch);
    };
  }, [location.pathname]);

  const handleMarkAsRead = async (id) => {
    try {
      await api.put(`/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n._id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error(err);
    }
  };

  let pageName = Object.entries(pageNames).find(([path]) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  })?.[1] || 'Verve Kitchens';

  const finalPageName = pageTitle || pageName;

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <header className={`fixed top-0 right-0 h-[72px] bg-white border-b border-gray-100 flex items-center justify-between px-6 z-30 transition-all duration-300 ${sidebarCollapsed ? 'left-16' : 'left-60'}`} style={{ boxShadow: '0 1px 0 #F0F0F0' }}>
      <div>
        <h1 className="font-display font-bold text-lg text-blum-dark leading-tight">{finalPageName}</h1>
        <p className="text-xs text-blum-slate font-body">{today}</p>
      </div>

      <div className="flex items-center gap-3">
        {/* Search */}
        {/* <div className="relative hidden md:block">
          <input
            type="text"
            placeholder="Search leads, projects..."
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blum-orange focus:ring-opacity-30 focus:border-blum-orange transition-all w-56 font-body placeholder-gray-400 outline-none"
          />
          <svg className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div> */}

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="relative w-9 h-9 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-blum-orange transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
              <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-red-500 border-2 border-white rounded-full"></span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-11 w-80 bg-white rounded-xl shadow-card-hover border border-gray-100 animate-fade-in z-50">
              <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <span className="font-display font-semibold text-sm text-blum-dark">Notifications</span>
                  <span className="badge bg-gray-50 text-gray-500 text-xs">{unreadCount} new</span>
                </div>
              </div>
              <div className="flex flex-col max-h-[300px] overflow-y-auto custom-scrollbar">
                {notifications.length === 0 ? (
                  <div className="p-6 flex flex-col items-center justify-center text-center">
                    <span className="text-2xl mb-2">📭</span>
                    <div className="text-sm font-medium text-blum-dark">No new notifications</div>
                    <div className="text-xs text-blum-slate mt-1">You're all caught up!</div>
                  </div>
                ) : (
                  notifications.map(notif => (
                    <div
                      key={notif._id}
                      className={`p-3 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors flex items-start gap-3 ${!notif.read ? 'bg-orange-50/30' : ''}`}
                      onClick={() => {
                        if (!notif.read) handleMarkAsRead(notif._id);
                        if (notif.link) {
                          setNotifOpen(false);
                          navigate(notif.link);
                        }
                      }}
                    >
                      <div className="flex-1">
                        <p className={`text-xs ${!notif.read ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>
                          {notif.message}
                        </p>
                        <span className="text-[10px] text-gray-400 mt-1 block">
                          {new Date(notif.createdAt).toLocaleDateString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-blum-orange rounded-full mt-1.5 flex-shrink-0"></div>
                      )}
                    </div>
                  ))
                )}
              </div>
              <div className="p-3 text-center border-t border-gray-100">
                <Link to="/notifications" onClick={() => setNotifOpen(false)} className="text-xs text-blum-orange font-medium hover:underline">View all notifications</Link>
              </div>
            </div>
          )}
        </div>

        {/* Avatar */}
        <div className="flex items-center gap-2.5 pl-3 border-l border-gray-100">
          <div className="w-8 h-8 bg-gradient-to-br from-blum-orange to-orange-400 rounded-full flex items-center justify-center shadow-orange">
            <span className="text-white text-xs font-bold font-display">{user?.name?.charAt(0)}</span>
          </div>
          <div className="hidden sm:block">
            <div className="text-xs font-semibold text-blum-dark leading-tight">{user?.name}</div>
            <div className="text-[10px] text-blum-slate capitalize">{user?.role?.replace(/_/g, ' ')}</div>
          </div>
        </div>
      </div>
    </header>
  );
};
